import { LiveValue, ProblemError, type Id } from '@/model';
import type { CallMedia } from './call-media';
import { isLiveCall, isParty, metadata, peerId, type CallApi, type CallKind, type CallMetadata, type CallSignal, type CallWire, type SignalPayload } from './call-wire';

export type CallPhase = 'outgoing' | 'incoming' | 'connecting' | 'active' | 'ended';
export type EndReason = 'hungUp' | 'remoteEnded' | 'cancelledByMe' | 'declinedByMe' | 'declined' | 'missed' | 'answeredElsewhere' | 'busy' | 'start' | 'accept' | 'media' | 'network' | 'server' | 'offline';
export interface CallViewState {
  call: CallMetadata | null; phase: CallPhase; endReason: EndReason | null;
  muted: boolean; pending: boolean; errorKey: string | null;
  connectedAt: number | null; endedAt: number | null;
  videoRequested: boolean; videoEnabled: boolean; remoteVideoEnabled: boolean;
  peerSupportsVideo: boolean | null; cameraFailure: boolean; foreground: boolean;
  connection: 'connecting' | 'connected' | 'disconnected' | 'failed';
}
interface Options { me: Id; direction: 'outgoing'|'incoming'; conversationId: Id; kind: CallKind; api: CallApi; media: CallMedia; now?: () => number; canSignal?: () => boolean }

/** One session, mirroring HyloCore CallEngine. Raw wire/ICE/SDP remain private and ephemeral. */
export class CallEngine {
  readonly state: LiveValue<CallViewState>;
  readonly media: CallMedia;
  readonly me: Id;
  readonly direction: 'outgoing'|'incoming';
  readonly conversationId: Id;
  readonly kind: CallKind;
  private readonly api: CallApi;
  private readonly now: () => number;
  private readonly canSignal: () => boolean;
  private call: CallWire | null = null;
  private started = false;
  private accepted = false;
  private prepared = false;
  private offered = false;
  private answerTaken = false;
  private answeredOffer = false;
  private remoteDescription = false;
  private offerPayload: SignalPayload | null = null;
  private candidates: SignalPayload[] = [];
  private pendingEvents: Array<CallWire | CallSignal> = [];
  private chain: Promise<void> = Promise.resolve();
  private cameraChain: Promise<void> = Promise.resolve();
  private videoAllowed = false;
  private videoEpoch = 0;
  private announcedVideo: boolean | undefined;
  private deadline?: ReturnType<typeof setTimeout>;
  private retired = false;

  constructor(options: Options) {
    this.me = options.me; this.direction = options.direction; this.conversationId = options.conversationId;
    this.kind = options.kind; this.api = options.api; this.media = options.media; this.now = options.now ?? Date.now;
    this.canSignal = options.canSignal ?? (() => true);
    this.state = new LiveValue<CallViewState>({ call: null, phase: options.direction, endReason: null, pending: false, errorKey: null, muted: false, connectedAt: null, endedAt: null, videoRequested: options.kind === 'video', videoEnabled: false, remoteVideoEnabled: false, peerSupportsVideo: null, cameraFailure: false, foreground: true, connection: 'connecting' });
    this.media.onCandidate = payload => { this.enqueue(async () => { if (this.call) await this.api.signal(this.call.id, 'candidate', payload).catch(() => undefined); }); };
    this.media.onConnection = connection => {
      if (this.ended) return;
      this.patch({ connection });
      if (connection === 'connected') { clearTimeout(this.deadline); this.patch({ phase: 'active', connectedAt: this.state.get().connectedAt ?? this.now() }); }
      if (connection === 'failed') this.fail('network');
    };
    this.media.onVideoFailure = () => this.videoUnavailable();
  }
  get ended() { return this.state.get().phase === 'ended'; }
  private patch(next: Partial<CallViewState>) { this.state.set({ ...this.state.get(), ...next }); }

  async start(clientId: Id): Promise<void> {
    if (this.started || this.ended || this.direction !== 'outgoing') return;
    this.started = true; this.patch({ pending: true });
    if (!this.canSignal()) { this.patch({errorKey:'error.offline'}); this.finish('offline'); return; }
    try {
      await this.media.acquireAudio();
      if (this.ended) { this.media.close(); return; }
      if (!this.canSignal()) { this.patch({errorKey:'error.offline'}); this.finish('offline'); return; }
      const call = await this.api.start({ client_id: clientId, conversation_id: this.conversationId, kind: this.kind === 'video' ? 'video' : 'voice' });
      if (this.ended) { if (!this.retired) void this.api.end(call.id).catch(() => undefined); return; }
      this.videoAllowed = true;
      this.update(call);
      this.replay(); this.scheduleVideo();
    } catch (error) {
      if (!this.ended) { this.patch({ errorKey: permissionError(error) ?? callErrorKey(error) }); this.finish(error instanceof ProblemError && error.code === 'call_busy' ? 'busy' : 'start'); }
    } finally { if (!this.ended) this.patch({ pending: false }); }
  }

  async accept(video?: boolean): Promise<void> {
    if (this.direction !== 'incoming' || this.state.get().phase !== 'incoming' || this.state.get().pending || !this.call) return;
    if (!this.canSignal()) { this.patch({errorKey:'error.offline'}); return; }
    this.patch({ pending: true, errorKey: null });
    try {
      await this.media.acquireAudio();
      if (this.ended) { this.media.close(); return; }
      if (!this.canSignal()) { this.patch({errorKey:'error.offline'}); this.fail('offline'); return; }
    } catch (error) { if (!this.ended) this.patch({ pending: false, errorKey: permissionError(error) ?? 'error.microphoneBusy' }); return; }
    if (video !== undefined) this.patch({ videoRequested: video });
    this.accepted = true; this.connecting();
    let answered: CallWire | undefined;
    for (let attempt = 0; attempt < 2 && !this.ended && !answered; attempt++) {
      try { answered = await this.api.accept(this.call.id); }
      catch (error) {
        if (error instanceof ProblemError && ['call_finished','call_not_ringing'].includes(error.code)) {
          try {
            const latest=await this.api.get(this.call.id);
            this.accepted=false;this.update(latest);
          } catch { /* One authoritative read is enough; cleanup stays bounded. */ }
          break;
        }
        if (error instanceof ProblemError && error.status < 500) break;
      }
    }
    if (this.ended) return;
    if (!answered) { this.fail('accept'); return; }
    this.update(answered);
    if (!this.ended) { this.videoAllowed = true; this.enqueue(() => this.prepare()); this.scheduleVideo(); this.patch({ pending: false }); }
  }

  update(update: CallWire): void {
    if (this.ended || !isParty(update, this.me) || update.conversation_id !== this.conversationId) return;
    if (this.call && this.call.id !== update.id) return;
    // An earlier REST ringing answer cannot regress a newer active update.
    if (this.call?.state === 'active' && update.state === 'ringing') return;
    this.call = { ...update, ice_servers: update.ice_servers.length ? update.ice_servers : this.call?.ice_servers ?? [] };
    this.patch({ call: metadata(this.call) });
    if (!isLiveCall(update)) {
      const reason: EndReason = update.state === 'declined' ? (this.direction === 'outgoing' ? 'declined' : 'declinedByMe')
        : update.state === 'cancelled' ? (this.direction === 'outgoing' ? 'cancelledByMe' : 'missed')
        : update.state === 'missed' ? 'missed' : update.state === 'failed' ? 'server' : 'remoteEnded';
      this.finish(reason); return;
    }
    if (update.state === 'active') {
      if (this.direction === 'incoming' && !this.accepted) { this.finish('answeredElsewhere'); return; }
      if (this.direction === 'outgoing' && this.state.get().phase === 'outgoing') { this.connecting(); this.enqueue(() => this.prepare()); }
    }
    this.replay();
  }

  event(event: CallWire | CallSignal): void {
    if (this.ended) return;
    if (!this.call && this.direction === 'outgoing') {
      if (this.pendingEvents.length < 512) this.pendingEvents.push(event);
      return;
    }
    if ('call_id' in event) this.signal(event); else this.update(event);
  }

  signal(signal: CallSignal): void {
    if (this.ended || !this.call || signal.call_id !== this.call.id || signal.from !== peerId(this.call, this.me)) return;
    const payload = signal.payload;
    switch (signal.type) {
      case 'offer':
        if (this.direction !== 'incoming' || this.offerPayload || this.answeredOffer || !payload.sdp) return;
        this.offerPayload = payload; this.bindVideo(payload);
        if (this.accepted) this.enqueue(() => this.answerOffer());
        break;
      case 'answer':
        if (this.direction !== 'outgoing' || this.answerTaken || !payload.sdp) return;
        this.answerTaken = true; this.bindVideo(payload);
        this.enqueue(async () => { if (!this.prepared || !payload.sdp) return; await this.media.applyAnswer(payload.sdp); if (this.ended) return; this.remoteDescription = true; await this.flushCandidates(); this.announceVideo(); });
        break;
      case 'candidate':
        if (!payload.candidate) return;
        if (this.remoteDescription) this.enqueue(() => this.media.addCandidate(payload).catch(() => undefined));
        else if (this.candidates.length < 256) this.candidates.push(payload);
        break;
      case 'media':
        if (this.state.get().peerSupportsVideo === true && typeof payload.video_enabled === 'boolean') this.patch({ remoteVideoEnabled: payload.video_enabled });
        break;
      case 'hangup': this.finish('remoteEnded'); break;
      case 'unknown': break;
    }
  }

  async hangUp(): Promise<void> {
    if (this.ended) return;
    const call = this.call, phase = this.state.get().phase;
    const decline = this.direction === 'incoming' && phase === 'incoming';
    this.finish(decline ? 'declinedByMe' : phase === 'outgoing' ? 'cancelledByMe' : 'hungUp');
    if (call && !this.retired) await (decline ? this.api.decline(call.id) : this.api.end(call.id)).catch(() => undefined);
  }
  dispose(): void { this.retired = true; if (!this.ended) this.finish('offline'); else this.media.close(); }
  abandon(): void { this.fail('offline'); }
  private fail(reason: EndReason): void {
    if (this.ended) return;
    const id = this.call?.id, ends = this.direction === 'outgoing' || this.state.get().connectedAt !== null;
    this.finish(reason);
    if (id && !this.retired) void (ends ? this.api.end(id) : this.api.decline(id)).catch(() => undefined);
  }
  private finish(reason: EndReason): void {
    if (this.ended) return;
    clearTimeout(this.deadline); this.videoEpoch++; this.videoAllowed = false;
    this.media.close(); this.call = null; this.offerPayload = null; this.candidates = []; this.pendingEvents = [];
    this.patch({ phase: 'ended', endReason: reason, endedAt: this.now(), pending: false, videoRequested: false, videoEnabled: false, remoteVideoEnabled: false });
  }
  private connecting(): void {
    if (this.ended || this.state.get().phase === 'active' || this.state.get().phase === 'connecting') return;
    this.patch({ phase: 'connecting' });
    this.deadline = setTimeout(() => { if (this.state.get().phase === 'connecting') this.fail('network'); }, 20_000);
  }
  private enqueue(step: () => Promise<void>): void {
    this.chain = this.chain.then(async () => { if (!this.ended) await step(); }).catch(() => this.fail('media'));
  }
  private async prepare(): Promise<void> {
    if (this.prepared || !this.call || this.ended) return;
    this.media.prepare(this.call.ice_servers,this.direction); this.prepared = true; this.media.setMuted(this.state.get().muted);
    if (this.direction === 'outgoing' && !this.offered) {
      this.offered = true;
      const sdp = await this.media.offer();
      if (!this.ended && this.call) { const payload = this.description(sdp); await this.api.signal(this.call.id, 'offer', payload); this.announcedVideo = payload.video_enabled; }
    } else if (this.direction === 'incoming') await this.answerOffer();
  }
  private async answerOffer(): Promise<void> {
    if (!this.prepared || !this.accepted || !this.offerPayload?.sdp || this.answeredOffer || !this.call || this.ended) return;
    this.answeredOffer = true;
    const sdp = await this.media.answer(this.offerPayload.sdp);
    if (this.ended || !this.call) return;
    this.remoteDescription = true; await this.flushCandidates();
    const payload = this.description(sdp); await this.api.signal(this.call.id, 'answer', payload); this.announcedVideo = payload.video_enabled; this.announceVideo();
  }
  private description(sdp: string): SignalPayload { return { sdp, video_capable: true, video_enabled: this.state.get().videoEnabled }; }
  private async flushCandidates(): Promise<void> {
    const candidates = this.candidates; this.candidates = [];
    for (const candidate of candidates) { if (this.ended) return; await this.media.addCandidate(candidate).catch(() => undefined); }
  }
  private replay(): void { const events = this.pendingEvents; this.pendingEvents = []; for (const event of events) this.event(event); }

  async setMuted(muted: boolean): Promise<void> {
    if (this.ended) return;
    if (!muted) { try { await this.media.acquireAudio(); } catch (error) { if (!this.ended) this.patch({ errorKey: permissionError(error) ?? 'error.microphoneBusy' }); return; } }
    if (!this.ended) { this.media.setMuted(muted); this.patch({ muted, errorKey: null }); }
  }
  setVideoEnabled(enabled: boolean): void {
    if (this.ended || this.state.get().peerSupportsVideo === false) return;
    this.videoEpoch++; this.patch({ videoRequested: enabled, cameraFailure: false });
    if (!enabled) this.stopVideo(); this.scheduleVideo();
  }
  setForeground(foreground: boolean): void {
    if (this.ended || foreground === this.state.get().foreground) return;
    this.videoEpoch++; this.patch({ foreground }); if (!foreground) this.stopVideo(); this.scheduleVideo();
  }
  videoUnavailable(): void {
    if (this.ended) return;
    this.videoEpoch++; this.patch({ videoRequested: false, cameraFailure: true }); this.stopVideo();
  }
  private bindVideo(payload: SignalPayload): void {
    if (this.state.get().peerSupportsVideo !== null) return;
    const capable = payload.video_capable === true;
    this.patch({ peerSupportsVideo: capable, remoteVideoEnabled: capable && payload.video_enabled === true });
    if (!capable) { this.videoEpoch++; this.patch({ videoRequested: false }); this.stopVideo(); }
  }
  private shouldCapture(): boolean { const s = this.state.get(); return !this.ended && this.videoAllowed && s.videoRequested && s.foreground && s.peerSupportsVideo !== false; }
  private stopVideo(): void { this.media.stopVideo(); this.patch({ videoEnabled: false }); this.announceVideo(); }
  private scheduleVideo(): void {
    this.cameraChain = this.cameraChain.then(async () => {
      if (!this.shouldCapture() || this.state.get().videoEnabled) return;
      const epoch = this.videoEpoch;
      try {
        await this.media.setVideoEnabled(true);
        if (this.ended || epoch !== this.videoEpoch || !this.shouldCapture()) { this.media.stopVideo(); return; }
        this.patch({ videoEnabled: true }); this.announceVideo();
      } catch { if (!this.ended && epoch === this.videoEpoch) this.videoUnavailable(); }
    });
  }
  private announceVideo(): void {
    if (this.ended || !this.remoteDescription || this.state.get().peerSupportsVideo !== true) return;
    this.enqueue(async () => {
      const enabled = this.state.get().videoEnabled;
      if (!this.call || this.announcedVideo === enabled) return;
      try { await this.api.signal(this.call.id, 'media', {video_enabled: enabled}); this.announcedVideo = enabled; } catch { /* Camera state retries on the next local operation; audio continues. */ }
    });
  }
}

export function permissionError(error: unknown): string | null {
  const name = typeof error==='object'&&error!==null&&'name' in error&&typeof error.name==='string'?error.name:'';
  return name === 'NotAllowedError' || name === 'SecurityError' ? 'error.microphoneHelp' : name === 'NotFoundError' ? 'error.noMicrophone' : name === 'NotReadableError' ? 'error.microphoneBusy' : null;
}
export function callErrorKey(error: unknown): string {
  if (error instanceof ProblemError) return ({ call_busy: 'state.busy', blocked: 'error.blocked', not_contacts: 'error.notContacts', call_unsupported: 'error.directPerson', realm_read_only: 'error.readOnly', realm_retired: 'error.retired', not_found: 'error.unavailable' } as Record<string,string>)[error.code] ?? 'state.failed';
  return 'error.connection';
}
